import React, { useMemo } from "react";
import { Card, ButtonGroup, Button, Row, Col, Placeholder } from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FaChartLine } from "react-icons/fa";
import { costBasisLabel, basisForViewMode, dailyCostOf, monthlyCostOf, quarterlyCostOf } from "./costBasis";
import AzureCostInsights from "./AzureCostInsights";

// The actual total is the one real number on this chart, so it gets its own color —
// near-black, matching every other fixed-hex chart color in this file (none of them are
// theme-reactive, so this doesn't invert in dark mode either — same as the rest). Moving
// averages are derived from that same number, so they share a small fixed set of accents
// instead of a rainbow, but the daily view can show three of them (30/60/90-day) at once —
// two of them the same hue differentiated only by dash pattern reads as one smear at this
// scale, not three lines, so each rolling window gets its own hue: orange (30-day, the one
// closest to "now"), the same neutral teal used on the KPI cards (60-day), and green
// (90-day, the smoothest/least reactive).
const COLOR_ACTUAL = "#212529";
const COLOR_AVERAGE_SHORT = "#cf7f00";
const COLOR_AVERAGE_MID = "#007681";
const COLOR_AVERAGE_LONG = "#949300";

function AzureCostCharts({ dailyData, monthlyData, quarterlyData, loading, viewMode, onViewModeChange }) {

  const basisLabel = costBasisLabel(basisForViewMode(viewMode));

  // Process daily data for time series chart - rolling 365 days with average cost per day
  const dailyChartData = useMemo(() => {
    if (!dailyData || dailyData.length === 0) return [];

    // Group by date and sum costs across subscriptions
    const byDate = {};
    dailyData.forEach((item) => {
      const date = item.costDate?.split("T")[0] || item.costDate;
      if (!byDate[date]) {
        byDate[date] = { date, totalCost: 0, subscriptions: {} };
      }
      const cost = dailyCostOf(item);
      byDate[date].totalCost += cost;
      byDate[date].subscriptions[item.subscriptionName] =
        (byDate[date].subscriptions[item.subscriptionName] || 0) + cost;
    });

    // Sort by date and take last 365 days
    const sortedData = Object.values(byDate)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-365);

    // Calculate rolling 30, 60, and 90-day averages
    return sortedData.map((item, index, arr) => {
      const calcAvg = (days) => {
        const start = Math.max(0, index - (days - 1));
        const window = arr.slice(start, index + 1);
        return window.reduce((sum, d) => sum + d.totalCost, 0) / window.length;
      };

      return {
        ...item,
        totalCost: parseFloat(item.totalCost.toFixed(2)),
        avg30Day: parseFloat(calcAvg(30).toFixed(2)),
        avg60Day: parseFloat(calcAvg(60).toFixed(2)),
        avg90Day: parseFloat(calcAvg(90).toFixed(2)),
      };
    });
  }, [dailyData]);

  // Process monthly data for bar chart - showing 3-month moving average
  const monthlyChartData = useMemo(() => {
    if (!monthlyData || monthlyData.length === 0) return [];

    // Group by year-month
    const byMonth = {};
    monthlyData.forEach((item) => {
      const key = `${item.year}-${String(item.month).padStart(2, "0")}`;
      if (!byMonth[key]) {
        byMonth[key] = { month: key, totalCost: 0 };
      }
      byMonth[key].totalCost += monthlyCostOf(item);
    });

    // Sort and take last 12 months
    const sortedData = Object.values(byMonth)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-12);

    // Calculate 3-month and 6-month moving averages
    return sortedData.map((item, index, arr) => {
      // 3-month moving average (current + up to 2 prior months)
      const windowStart3 = Math.max(0, index - 2);
      const window3 = arr.slice(windowStart3, index + 1);
      const movingAvg3 = window3.reduce((sum, m) => sum + m.totalCost, 0) / window3.length;

      // 6-month moving average (current + up to 5 prior months)
      const windowStart6 = Math.max(0, index - 5);
      const window6 = arr.slice(windowStart6, index + 1);
      const movingAvg6 = window6.reduce((sum, m) => sum + m.totalCost, 0) / window6.length;

      return {
        month: item.month,
        totalCost: parseFloat(item.totalCost.toFixed(2)),
        movingAvg3: parseFloat(movingAvg3.toFixed(2)),
        movingAvg6: parseFloat(movingAvg6.toFixed(2)),
      };
    });
  }, [monthlyData]);

  // Process quarterly data for bar chart - showing 2-quarter moving average
  const quarterlyChartData = useMemo(() => {
    if (!quarterlyData || quarterlyData.length === 0) return [];

    // Group by year-quarter
    const byQuarter = {};
    quarterlyData.forEach((item) => {
      const key = `${item.year}-Q${item.quarter}`;
      if (!byQuarter[key]) {
        byQuarter[key] = { quarter: key, totalCost: 0 };
      }
      byQuarter[key].totalCost += quarterlyCostOf(item);
    });

    // Take last 8 quarters (2 years)
    const sortedData = Object.values(byQuarter)
      .sort((a, b) => a.quarter.localeCompare(b.quarter))
      .slice(-8);

    // Calculate 2-quarter moving average
    return sortedData.map((item, index, arr) => {
      // Get previous quarters for moving average (current + 1 prior quarter)
      const windowStart = Math.max(0, index - 1);
      const window = arr.slice(windowStart, index + 1);
      const movingAvg = window.reduce((sum, q) => sum + q.totalCost, 0) / window.length;

      return {
        quarter: item.quarter,
        totalCost: parseFloat(item.totalCost.toFixed(2)),
        movingAvg: parseFloat(movingAvg.toFixed(2)),
      };
    });
  }, [quarterlyData]);

  // Custom tooltip formatter for currency
  const currencyFormatter = (value) => `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border rounded shadow-sm">
          <p className="mb-1"><strong>{label}</strong></p>
          {payload.map((entry, index) => (
            <p key={index} className="mb-0" style={{ color: entry.color }}>
              {entry.name}: ${entry.value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Shared across all three render paths (loading/empty/main) so the view mode stays
  // switchable even while this particular mode has nothing to show yet.
  const viewModeToggle = (
    <ButtonGroup size="sm">
      <Button
        variant={viewMode === "daily" ? "primary" : "outline-primary"}
        onClick={() => onViewModeChange("daily")}
      >
        Daily
      </Button>
      <Button
        variant={viewMode === "monthly" ? "primary" : "outline-primary"}
        onClick={() => onViewModeChange("monthly")}
      >
        Monthly
      </Button>
      <Button
        variant={viewMode === "quarterly" ? "primary" : "outline-primary"}
        onClick={() => onViewModeChange("quarterly")}
      >
        Quarterly
      </Button>
    </ButtonGroup>
  );

  if (loading) {
    return (
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">Cost Analytics</h5>
            {viewModeToggle}
          </div>
        </Card.Header>
        <Card.Body>
          <Placeholder as="div" animation="glow">
            <Placeholder xs={12} size="lg" style={{ height: "450px" }} />
          </Placeholder>
        </Card.Body>
      </Card>
    );
  }

  const hasData =
    (viewMode === "daily" && dailyChartData.length > 0) ||
    (viewMode === "monthly" && monthlyChartData.length > 0) ||
    (viewMode === "quarterly" && quarterlyChartData.length > 0);

  if (!hasData) {
    return (
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">Cost Analytics</h5>
            {viewModeToggle}
          </div>
        </Card.Header>
        <Card.Body>
          <div className="text-center text-muted py-4">
            <FaChartLine size={48} className="mb-3 opacity-50" />
            <p>No cost data available to display charts</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <Card.Header>
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">Cost Analytics <small className="text-muted">({basisLabel})</small></h5>
          {viewModeToggle}
        </div>
      </Card.Header>
      <Card.Body>
        <Row>
          {/* Main Cost Trend Chart */}
          <Col lg={8}>
            <h6 className="text-muted mb-3">
              {viewMode === "daily" && `Daily ${basisLabel} Cost Trend (Last 365 Days)`}
              {viewMode === "monthly" && `Monthly ${basisLabel} Cost Trend (Last 12 Months)`}
              {viewMode === "quarterly" && `Quarterly ${basisLabel} Cost Trend (Last 8 Quarters)`}
            </h6>
            <ResponsiveContainer width="100%" height={450}>
                <LineChart
                  data={
                    viewMode === "daily"
                      ? dailyChartData
                      : viewMode === "monthly"
                      ? monthlyChartData
                      : quarterlyChartData
                  }
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={viewMode === "daily" ? "date" : viewMode === "monthly" ? "month" : "quarter"}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis yAxisId="left" tickFormatter={currencyFormatter} />
                  {viewMode === "daily" && (
                    <YAxis yAxisId="right" orientation="right" tickFormatter={currencyFormatter} />
                  )}
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="totalCost"
                    stroke={COLOR_ACTUAL}
                    strokeWidth={viewMode === "daily" ? 1 : 2}
                    dot={viewMode === "daily" ? false : { r: 3 }}
                    name={`Total ${basisLabel} Cost`}
                    yAxisId={viewMode === "daily" ? "right" : "left"}
                  />
                  {viewMode === "daily" && (
                    <>
                      <Line
                        type="monotone"
                        dataKey="avg30Day"
                        stroke={COLOR_AVERAGE_SHORT}
                        strokeWidth={2}
                        dot={false}
                        name="30-Day Rolling Avg"
                        yAxisId="left"
                      />
                      <Line
                        type="monotone"
                        dataKey="avg60Day"
                        stroke={COLOR_AVERAGE_MID}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                        name="60-Day Rolling Avg"
                        yAxisId="left"
                      />
                      <Line
                        type="monotone"
                        dataKey="avg90Day"
                        stroke={COLOR_AVERAGE_LONG}
                        strokeWidth={2}
                        strokeDasharray="3 3"
                        dot={false}
                        name="90-Day Rolling Avg"
                        yAxisId="left"
                      />
                    </>
                  )}
                  {viewMode === "monthly" && (
                    <>
                      <Line
                        type="monotone"
                        dataKey="movingAvg3"
                        stroke={COLOR_AVERAGE_SHORT}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ r: 3 }}
                        name="3-Month Moving Avg"
                        yAxisId="left"
                      />
                      <Line
                        type="monotone"
                        dataKey="movingAvg6"
                        stroke={COLOR_AVERAGE_LONG}
                        strokeWidth={2}
                        strokeDasharray="3 3"
                        dot={{ r: 3 }}
                        name="6-Month Moving Avg"
                        yAxisId="left"
                      />
                    </>
                  )}
                  {viewMode === "quarterly" && (
                    <Line
                      type="monotone"
                      dataKey="movingAvg"
                      stroke={COLOR_AVERAGE_SHORT}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3 }}
                      name="2-Quarter Moving Avg"
                      yAxisId="left"
                    />
                  )}
                </LineChart>
            </ResponsiveContainer>
          </Col>

          {/* Summary cards, in the column the subscription pie used to occupy */}
          <Col lg={4}>
            <AzureCostInsights dailyData={dailyData} monthlyData={monthlyData} loading={false} columns={12} />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

export default AzureCostCharts;
